import { useState, ReactElement } from 'react';

import Chip from '@mui/material/Chip';
import classNames from 'classnames';

import { Box } from '../../Box';
import { Tooltip } from '../../Tooltip';
import { Typography } from '../../Typography';
import { EllipsisText } from '../../EllipsisText';
import { Icon } from '../../Icon';

import { useStyles } from './SelectedOptionsInput.styles';

/**
 * Опция для компонента {@link SelectedOptionsInput}
 */
interface Option {
  /**
   * Надпись опции.
   */
  label: string;

  /**
   * Значение опции.
   */
  value: string;

  /**
   * Необязательная информация для всплывающей подсказки.
   */
  tooltip?: {
    title: string;
    subtitle?: string;
  };

  /**
   * Необязательный префикс для отображения перед надписью опции.
   */
  prefix?: string;

  /**
   * Указывает, находится ли опция в состоянии ошибки.
   */
  error?: boolean;

  /**
   * Указывает, находится ли опция в состоянии предупреждения.
   */
  warning?: boolean;
}

/**
 * Свойства компонента {@link SelectedOptionsInput}
 */
interface SelectedOptionsInputProps {
  /**
   * Список выбранных опций для отображения.
   */
  options: Option[];

  /**
   * Функция обратного вызова, которая вызывается при удалении опции.
   */
  onDelete?: (option: Option) => void;

  /**
   * Пользовательская функция рендеринга для всплывающей подсказки.
   */
  renderTooltip?: (option: Option) => ReactElement;

  /**
   * Максимальное количество опций для отображения на странице.
   */
  limit?: number;

  /**
   * Максимальная ширина компонента.
   */
  maxWidth?: number | string;

  /**
   * Указывает, следует ли отображать всплывающие подсказки для опций.
   */
  withTooltip?: boolean;

  /**
   * Флаг, указывающий, следует ли отображать границу.
   */
  withBorder?: boolean;

  /**
   * Флаг, указывающий, отключены опции или нет.
   */
  disabledOptions?: boolean;

  /**
   * Флаг, указывающий, следует ли отображать кнопку "Показать все".
   */
  showAllOptionsButton?: boolean;
}

/**
 * Компонент SelectedOptionsInput используется для отображения списка выбранных опций.
 * Он поддерживает удаление опций, всплывающие подсказки (tooltips) и постраничную навигацию.
 *
 * @public
 * @param options - Список выбранных опций для отображения.
 * @param onDelete - Функция обратного вызова, которая вызывается при удалении опции.
 * @param renderTooltip - Пользовательская функция рендеринга для всплывающей подсказки.
 * @param limit - Максимальное количество опций для отображения на странице.
 * @param maxWidth - Максимальная ширина компонента.
 * @param withTooltip - Указывает, следует ли отображать всплывающие подсказки для опций.
 * @param withBorder - Флаг, указывающий, следует ли отображать границу.
 * @param disabledOptions - Флаг, указывающий, отключены опции или нет.
 * @param showAllOptionsButton - Флаг, указывающий, следует ли отображать кнопку "Показать все".
 * @returns Компонент SelectedOptionsInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link SelectedOptionsInputProps}
 */
export const SelectedOptionsInput = ({
  options,
  onDelete,
  limit = 15,
  renderTooltip,
  withTooltip,
  withBorder,
  maxWidth,
  disabledOptions,
  showAllOptionsButton = true,
}: SelectedOptionsInputProps) => {
  const { classes } = useStyles({ maxWidth });
  const [page, setPage] = useState(1);

  const shownOptions = options.slice(0, limit * page);

  const handleOptionDelete = (option: Option) => {
    onDelete?.(option);
  };

  return (
    <Box display="flex" flexDirection="column" gap="8">
      <Box display="flex" flexWrap="wrap" maxWidth="100%" gap="8">
        {shownOptions.map((option) => (
          <Box key={option.value} maxWidth="100%">
            <Tooltip
              disabled={!withTooltip}
              title={renderTooltip ? renderTooltip(option) : option.label}
              placement="top"
            >
              <Chip
                data-testid={`selected-option-${option.value}`}
                classes={{
                  root: classNames(
                    classes.chipInput,
                    withBorder && classes.withBorder,
                  ),
                  deleteIcon: classNames({
                    [classes.iconError]: option.error,
                    [classes.iconWarning]: option.warning && !option.error,
                  }),
                }}
                label={
                  <EllipsisText rows={1}>
                    <Typography color="inherit" variant="body3">
                      {`${option.prefix ? `${option.prefix}, ` : ''}${
                        option.label
                      }`}
                    </Typography>
                  </EllipsisText>
                }
                onDelete={() => handleOptionDelete(option)}
                deleteIcon={
                  !disabledOptions ? (
                    <Icon
                      data-testid={`selected-option-delete-${option.value}`}
                      name="cancel"
                      size="20"
                    />
                  ) : undefined
                }
                className={classNames(
                  classes.chip,
                  option.error && classes.chipError,
                  option.warning && classes.chipWarning,
                  disabledOptions && classes.chipDisabled,
                )}
              />
            </Tooltip>
          </Box>
        ))}
      </Box>
      {options.length > limit && (
        <Box display="flex" gap="8">
          {options.length > shownOptions.length && (
            <Box
              className={classes.button}
              onClick={() => setPage((currentPage) => currentPage + 1)}
            >
              <Typography color="inherit" variant="body3">
                Показать еще
              </Typography>
              <Icon name="more_horizontal" size="20" />
            </Box>
          )}
          {showAllOptionsButton && options.length > shownOptions.length && (
            <Box
              className={classes.button}
              onClick={() => setPage(Math.ceil(options.length / limit))}
            >
              <Typography color="inherit" variant="body3">
                Показать все {options.length}
              </Typography>
              <Icon name="square_4_outline" size="20" />
            </Box>
          )}
          {options.length <= shownOptions.length && options.length > limit && (
            <Box className={classes.button} onClick={() => setPage(1)}>
              <Typography color="inherit" variant="body3">
                Скрыть
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
